/* eslint-disable max-lines */
// @ts-strict-ignore
import { stripTags } from 'underscore.string';
import { captureException } from '@sentry/nextjs';
import Router from 'next/router';
import { classifierPost, get, getServerSideConfig, post, put } from 'src/utils/api';
import i18n from 'src/utils/translate';
import { stripBBTags } from 'src/utils/string';
import { composeHtmlStringFromBB } from 'src/components/textEditor/deserialiseContent';
import actionTypes from 'src/modules/community/write/actionTypes';
import { setAlert } from 'src/components/alert/actions';
import { fetchCommunityTopics } from 'src/modules/community/shared/actions';
import { ReduxState } from 'src/store/store';

export const setPostTopic = (topicId: number) => {
  return async (dispatch, getState: () => ReduxState) => {
    const slug = getState().context.currentCommunity?.slug;
    if (!slug) return;
    await dispatch(fetchCommunityTopics(slug));
    const topics = getState().context.currentCommunity.topics;
    const selectedTopic = topics.find(({ id }) => id === topicId);
    dispatch({
      type: actionTypes.SET_TOPIC,
      topic: topicId === -1 ? null : selectedTopic,
    });
  };
};

export const setPostPrivate = (isPostPrivate: boolean) => {
  return async (dispatch) => dispatch({ type: actionTypes.SET_PRIVATE, isPostPrivate });
};

export const getTags = (title: string, body: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    const { language, communityId } = getState().context.currentCommunity;
    try {
      if (title?.length || body?.length) {
        const response = await classifierPost('autotag', {
          title: title && stripBBTags(title) && stripTags(title) || '', // in case users manually added BB
          body: body && stripTags(body) && stripBBTags(body) || '',
          groupId: communityId,
          lang: language || 'en',
        });
        const excludedTags = getState().pages.write.excludedTags;
        const displayedTags = response.data.entities.filter(entity => entity.isDisplayed && !excludedTags.includes(entity.entityId)) || [];
        dispatch({ type: actionTypes.SET_TAGS, tags: displayedTags });
      }
    } catch (ex) {
      captureException(ex);
      dispatch(setAlert());
    }
  };
};

export const addExcludedTag = (entityId) => async (dispatch) => dispatch({ type: actionTypes.EXCLUDE_TAG, entityId });

const uploadImageFromFile = (file: Blob) => {
  return async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: { imageHash } } = await post('private/images/post', formData);
      if (imageHash) dispatch({ type: actionTypes.IMAGE_UPLOAD_SUCCESS, hash: imageHash });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.IMAGE_UPLOAD_FAILURE, error: ex });
      if (ex.message.includes('413')) {
        dispatch(setAlert(i18n.t('Looks like your file exceeds our 10MB limit.')));
      } else {
        dispatch(setAlert(i18n.t('There was a problem uploading your image. Make sure your image ends with .jpg, .jpeg or .png.'), 'danger', 'none'));
      }
    }
  };
};

type CreatePostProps = {
  title: string,
  body: string,
  image?: { file?: Blob, url?: string, hash?: string, description?: string },
  mentionedUserIds?: number[],
}
export const createPost = ({ title, body, image, mentionedUserIds }: CreatePostProps) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.WRITE_A_POST });
    if (image?.file) {
      await dispatch(uploadImageFromFile(image.file));
    }
    const state = getState();
    const { slug, isPrivate } = state.context.currentCommunity;
    const { excludedTags, selectedTopic, isPostPrivate, newImageHash, imageUploadError } = state.pages.write;
    if (imageUploadError) {
      dispatch({ type: actionTypes.WRITE_A_POST_FAILURE });
      return;
    }
    try {
      const { data: { id, urlEncodedTitle } } = await post(`private/posts/${slug}`, {
        title,
        body,
        isPrivate: isPostPrivate || isPrivate,
        ...(selectedTopic && { categoryId: selectedTopic.id }),
        ...(newImageHash && { postImageHash: newImageHash }),
        ...(mentionedUserIds && { mentionedUserIds }),
        ...(excludedTags && { excludeEntities: excludedTags }),
      });
      if (id) {
        dispatch({ type: actionTypes.WRITE_A_POST_SUCCESS });
        dispatch(setAlert(i18n.t('New post added!'), 'success'));
        const privateUrl = isPostPrivate ? '/private' : '';
        Router.push(`/${slug}/posts${privateUrl}/${id}/${urlEncodedTitle}`);
      } else {
        dispatch({ type: actionTypes.WRITE_A_POST_FAILURE });
        dispatch(setAlert());
      }
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.WRITE_A_POST_FAILURE });
      const message = ex.response?.status === 403 ? i18n.t('Permission denied.') : undefined;
      dispatch(setAlert(message, 'danger', 'none'));
    }
  };
};

export const editPost = ({ title, body, image, mentionedUserIds }: CreatePostProps) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.EDIT_A_POST });
    if (image?.file) {
      await dispatch(uploadImageFromFile(image.file));
    }
    const state = getState();
    const { slug, isPrivate } = state.context.currentCommunity;
    const { post, excludedTags, selectedTopic, isPostPrivate, newImageHash, imageUploadError } = state.pages.write;
    if (imageUploadError) {
      dispatch({ type: actionTypes.EDIT_A_POST_FAILURE });
      return;
    }
    try {
      const postImageHash = newImageHash || image?.hash; // has to be from image, as the user could have deleted an image and not added a new one
      const { data: { id, urlEncodedTitle } } = await put(`private/posts/${slug}/${post.id}`, {
        title,
        body,
        isPrivate: isPostPrivate || isPrivate,
        ...(selectedTopic && { categoryId: selectedTopic.id }),
        ...(postImageHash && { postImageHash }),
        ...(mentionedUserIds && { mentionedUserIds }),
        ...(excludedTags && { excludeEntities: excludedTags }),
      });
      if (id) {
        dispatch({ type: actionTypes.EDIT_A_POST_SUCCESS });
        dispatch(setAlert(i18n.t('Edit a post successful!'), 'success'));
        const privateUrl = isPostPrivate ? '/private' : '';
        Router.push(`/${slug}/posts${privateUrl}/${id}/${urlEncodedTitle}`);
      } else {
        dispatch({ type: actionTypes.EDIT_A_POST_FAILURE });
        dispatch(setAlert());
      }
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.EDIT_A_POST_FAILURE });
      const message = ex.response?.status === 403 ? i18n.t('Permission denied.') : undefined;
      dispatch(setAlert(message, 'danger', 'none'));
    }
  };
};

// same url as fetchPost in SPP - but different handling
export const fetchPostForEdit = (postId, communitySlug) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.FETCH_POST_EDIT });
    try {
      // get private - because to edit, one must be a community member
      const { data } = await get(`private/posts/${communitySlug}/${postId}`, getServerSideConfig(getState().context.sessionId));
      const htmlTitleString = composeHtmlStringFromBB({ text: data.title, isTitle: true });
      const htmlBodyString = composeHtmlStringFromBB({ text: data.body });
      if (data.categoryId) await dispatch(setPostTopic(data.categoryId));
      dispatch({
        type: actionTypes.FETCH_POST_EDIT_SUCCESS,
        post: data,
        htmlTitleString,
        htmlBodyString,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.FETCH_POST_EDIT_FAILURE, error: ex });
      let message = ex.message;
      if (ex.response?.data?.message) message = ex.response?.data?.message;
      dispatch(setAlert(message));
    }
  };
};

export const fetchPollForEdit = (pollId, communitySlug) => {
  return async (dispatch, getState: () => ReduxState) => {
    dispatch({ type: actionTypes.FETCH_POLL_EDIT });
    try {
      // get private - because to edit, one must be a community member
      const { data } = await get(`private/polls/${communitySlug}/${pollId}`, getServerSideConfig(getState().context.sessionId));
      dispatch({ type: actionTypes.FETCH_POLL_EDIT_SUCCESS, poll: data });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: actionTypes.FETCH_POLL_EDIT_FAILURE, error: ex });
      let message = ex.message;
      if (ex.response?.data?.message) message = ex.response?.data?.message;
      dispatch(setAlert(message));
    }
  };
};

export const showPollPage = (isPoll: boolean) => (dispatch) => dispatch({ type: actionTypes.SHOW_POLL_PAGE, isPoll });

export const setIsEdited = () => (dispatch) => dispatch({ type: actionTypes.SET_IS_EDITED });

export const resetWritePage = () => (dispatch) => dispatch({ type: actionTypes.RESET_WRITE_PAGE });
